import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${window.location.origin}`;
const AuthContext = createContext(null);

const isPreviewHost = () =>
  window.location.hostname.includes("preview.emergentagent.com");

async function fetchPreviewSession() {
  // Always refreshes a preview JWT and validates it via /me.
  // Returns the user object on success, null on failure.
  const tokenResp = await axios.get(`${API}/api/auth/preview-token`);
  const token = tokenResp.data.token;
  localStorage.setItem("auth_token", token);
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  const meResp = await axios.get(`${API}/api/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return meResp.data;
}

const FALLBACK_PRINCIPAL = {
  role: "admin",
  role_label: "Admin",
  is_admin: true,
  access: { sales: "write", estimating: "write" },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [principal, setPrincipal] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPrincipal = useCallback(async (token) => {
    try {
      const resp = await axios.get(`${API}/api/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrincipal(resp.data);
      localStorage.setItem("auth_principal", JSON.stringify(resp.data));   // FLD-OFF: reused offline
    } catch (e) {
      const cachedP = localStorage.getItem("auth_principal");
      if (!e?.response && cachedP) {          // no signal → the last known access, not the fallback
        try { setPrincipal(JSON.parse(cachedP)); return; } catch { /* fall through */ }
      }
      // 403 = deactivated or never invited: no app for them at all.
      if (e?.response?.status === 403) {
        setPrincipal({ denied: true, reason: e.response?.data?.detail || "",
                       access: { sales: "none", estimating: "none" } });
      } else {
        setPrincipal(FALLBACK_PRINCIPAL);
      }
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("auth_token");

    // Try the existing token first if we have one.
    if (token) {
      try {
        const resp = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(resp.data);
        localStorage.setItem("auth_user", JSON.stringify(resp.data));   // FLD-OFF: the offline session
        await loadPrincipal(token);
        setLoading(false);
        return;
      } catch (e) {
        // FLD-OFF §1: no signal is not "signed out" — keep the last verified session so the
        // installed app opens on the cached views. Only a real 401/403 clears the token.
        const cached = localStorage.getItem("auth_user");
        if (!e?.response && cached) {
          try { setUser(JSON.parse(cached)); } catch { /* fall through */ }
          const p = localStorage.getItem("auth_principal");
          if (p) { try { setPrincipal(JSON.parse(p)); } catch { /* ignore */ } }
          setLoading(false);
          return;
        }
        // Existing token is invalid — clear and fall through to preview auto-login.
        localStorage.removeItem("auth_token");
      }
    }

    // Preview hostname: silently mint a fresh session every time.
    if (isPreviewHost()) {
      try {
        const me = await fetchPreviewSession();
        setUser(me);
        localStorage.setItem("auth_user", JSON.stringify(me));   // FLD-OFF: the offline session
        await loadPrincipal(localStorage.getItem("auth_token"));
      } catch {
        // Preview auto-login failed (rare). Fall through to login page.
      }
    }

    setLoading(false);
  }, [loadPrincipal]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (token) => {
    localStorage.setItem("auth_token", token);
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    checkAuth();
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(`${API}/api/auth/logout`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // ignore
    }
    localStorage.removeItem("auth_token");
    setUser(null);

    // On preview, immediately mint a fresh session so "Sign out" doesn't lock the user out.
    if (isPreviewHost()) {
      try {
        const me = await fetchPreviewSession();
        setUser(me);
      } catch {
        // ignore — user can manually refresh
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        principal,
        role: principal?.role || null,
        roleLabel: principal?.role_label || null,
        isAdmin: !!principal?.is_admin,
        denied: !!principal?.denied,
        deniedReason: principal?.reason || "",
        access: principal?.access || FALLBACK_PRINCIPAL.access,
        landing: principal?.landing || null,
        liveTabs: principal?.live_tabs || null,
        refreshPrincipal: () => loadPrincipal(localStorage.getItem("auth_token")),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
